import { createAsyncThunk } from '@reduxjs/toolkit'

import { RootState } from 'app/store'
import { fetchEdgeDataThunk, makeFetchSlice } from 'app/redux-fetch'
import { fetchDomain, fetchDomains } from 'features/domains/api'
import type { DomainType } from 'features/domains/types'

import {
  domainToSubDomainsCacheSelector,
  domainToDomainDataCacheSelector,
  selectedNavTopDomainIdSelector,
} from 'features/domains/selectors'

import { DOMAIN_SUB_DOMAIN_FETCH_DATA } from './consts'

export const domainSubDomainsFetchSlice = makeFetchSlice(
  DOMAIN_SUB_DOMAIN_FETCH_DATA
)
const domainSubDomainsFetchActions = domainSubDomainsFetchSlice.actions

export const fetchDomainSubDomainsThunk = createAsyncThunk(
  'domains/fetchDomainSubDomains',
  async (domainId: string, { dispatch }) => {
    try {
      const response: DomainType[] = await fetchEdgeDataThunk({
        dispatch,
        actions: domainSubDomainsFetchActions,
        asyncFunc: () => fetchDomains({ domainid: domainId }),
      })
      //console.log('fetchDomainSubDomainsThunk-fetch')
      const subDomains =
        response[0]?.subDomains?.filter((domain) => !domain.job) ?? []

      return subDomains
    } catch (error) {
      console.log('Error fetching domain sub-domains', error)
      return []
    }
  }
)

const domainSlice = makeFetchSlice('domain')
const domainActions = domainSlice.actions

export const fetchDomainThunk = createAsyncThunk<DomainType, string>(
  'domains/fetchDomain',
  async (domainId: string, { dispatch }) => {
    try {
      const response = await fetchEdgeDataThunk({
        dispatch,
        actions: domainActions,
        asyncFunc: () => fetchDomain(domainId),
      })

      return response
    } catch (error) {
      console.log('Error fetching domain', error)
    }
  }
)

function getPathToRootDomain(domain?: DomainType): string[] {
  if (!domain) {
    return []
  }

  const { id, parent } = domain

  return [id, ...getPathToRootDomain(parent)]
}

interface GetDomainSubDomainsThunkParams {
  domainId: string
  forceUpdate?: boolean
}

export const getDomainSubDomainsThunk = createAsyncThunk<
  [string[], DomainType[][], DomainType] | null,
  GetDomainSubDomainsThunkParams,
  { state: RootState }
>(
  'domains/getDomainSubDomains',
  async ({ domainId, forceUpdate }, { dispatch, getState }) => {
    const state = getState()
    const domainsCache = domainToDomainDataCacheSelector(state)
    const subDomainsCache = domainToSubDomainsCacheSelector(state)
    const currentUserDomainId = selectedNavTopDomainIdSelector(state)

    const domain =
      domainsCache.hasOwnProperty(domainId) && !forceUpdate
        ? domainsCache[domainId]
        : await dispatch(fetchDomainThunk(domainId)).unwrap()

    if (!domain) {
      return null
    }

    const pathToRootDomain = getPathToRootDomain(domain).reverse()
    const pathToUserDomain = pathToRootDomain.slice(
      pathToRootDomain.findIndex((elem) => elem === currentUserDomainId)
    )
    const subDomains = await Promise.all(
      pathToUserDomain.map((domainId) => {
        if (subDomainsCache.hasOwnProperty(domainId)) {
          if (!forceUpdate) {
            return subDomainsCache[domainId] ?? []
          }
        }
        //console.log('subDomains')
        return dispatch(fetchDomainSubDomainsThunk(domainId)).unwrap()
      })
    )

    const existingSubDomains = subDomains.filter((list) => list.length)

    return [pathToUserDomain, existingSubDomains, domain]
  }
)
